/**
 * Particles Background Component
 * 
 * A component that creates an interactive particle background effect using tsparticles.
 * Particles move and interact with mouse hover, creating a dynamic cosmic effect.
 * 
 * Features:
 * - Interactive particle system
 * - Mouse hover interactions
 * - Smooth animations
 * - Customizable particle properties
 * - Performance optimized
 */

'use client';

import { useCallback } from 'react';
import Particles from 'react-particles';
import type { Engine } from 'tsparticles-engine';
import { loadSlim } from 'tsparticles-slim';
import { useTheme } from 'next-themes';

/**
 * Particles Background Component
 * 
 * @component
 * @returns {JSX.Element} The particles background component
 * 
 * @example
 * <ParticlesBackground />
 */
export default function ParticlesBackground(): JSX.Element {
	const { resolvedTheme } = useTheme();
	const isLight = resolvedTheme === 'light';

	/**
	 * Initializes the particles engine
	 * @async
	 * @param {Engine} engine - The tsparticles engine instance
	 */
	const particlesInit = useCallback(async (engine: Engine) => {
		await loadSlim(engine);
	}, []);

	return (
		<div className="absolute inset-0 pointer-events-none">
			<Particles
				key={resolvedTheme}
				id="cosmic-particles"
				init={particlesInit}
				options={{
					fullScreen: {
						enable: true,
						zIndex: 1,
					},
					particles: {
						number: {
							value: 100,
							density: {
								enable: true,
								value_area: 800,
							},
						},
						color: {
							value: isLight ? '#5C5CA8' : '#ffffff',
						},
						shape: {
							type: 'circle',
						},
						opacity: {
							value: isLight ? 0.25 : 0.5,
							random: true,
							animation: {
								enable: true,
								speed: 1,
								minimumValue: 0.1,
								sync: false,
							},
						},
						size: {
							value: 2,
							random: true,
							animation: {
								enable: true,
								speed: 2,
								minimumValue: 0.1,
								sync: false,
							},
						},
						move: {
							enable: true,
							speed: 0.5,
							direction: 'none',
							random: true,
							straight: false,
							outModes: {
								default: 'out',
							},
							attract: {
								enable: true,
								rotateX: 600,
								rotateY: 1200,
							},
						},
					},
					interactivity: {
						detectsOn: 'canvas',
						events: {
							onHover: {
								enable: true,
								mode: 'grab',
							},
							resize: true,
						},
						modes: {
							grab: {
								distance: 140,
								links: {
									opacity: 0.5,
								},
							},
						},
					},
					background: {
						color: {
							value: 'transparent',
						},
					},
				}}
			/>
		</div>
	);
}
